/**
 * @fileoverview User table component with CRUD actions
 * Uses DmsEntityTable for consistent behavior across the application
 */
import React, { forwardRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { intl, DmsButton, DmsButtonGroup } from "dmsbasereact";
import { Box, Chip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

import BlockIcon from "@mui/icons-material/Block";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import { DmsEntityTable } from "../DmsEntityTable";
import { ACCOUNT_STATUS_OPTIONS } from "../../constants/UserManagementConstants";

/** @type {Object.<string, string>} User type to create button label map */
const CREATE_BUTTON_LABELS = {
  default: "CreateUser",
  sso: "CreateSsoUser",
  doctor: "CreatePrescriber",
  pharmacist: "CreateDispenser",
  person: "CreateResponsible",
  auditor: "CreateAuditor",
};

/** @type {Object.<string, string>} Default labels for create buttons */
const CREATE_BUTTON_DEFAULTS = {
  CreateUser: "Create User",
  CreateSsoUser: "Create SSO User",
  CreatePrescriber: "Create Prescriber",
  CreateDispenser: "Create Dispenser",
  CreateResponsible: "Create Responsible",
  CreateAuditor: "Create Auditor",
};

/** @type {Object.<string, string>} Map frontend user types to backend filter values */
const USER_TYPE_TO_BACKEND = {
  sso: "internal",
  doctor: "doctor",
  pharmacist: "pharmacist",
  person: "person",
  auditor: "auditor",
};

/**
 * Account status color mapping
 */
const ACCOUNT_STATUS_COLORS = {
  1: "info",       // PENDING_CFM_VALIDATION
  2: "info",       // PENDING_DOCUMENT_UPLOAD
  3: "warning",    // PENDING_APPROVAL (Legacy)
  4: "info",       // PENDING_TRAINING
  5: "success",    // ACTIVE
  6: "error",      // REJECTED
  7: "default",    // SUSPENDED
  8: "warning",    // PENDING_APPROVAL_GESTAO_LENA
  9: "warning",    // PENDING_APPROVAL_REGULATORIO
};

/**
 * Renders status chip based on user state
 * @param {object} row - User row data
 * @returns {JSX.Element} Status chip
 */
const getStatusChip = (row) => {
  if (row.is_blocked) {
    return <Chip label={intl.get("Blocked").d("Blocked")} color="error" size="small" />;
  }

  // For external users, show account_status_label if available
  if (row.is_external && row.account_status_label) {
    const color = ACCOUNT_STATUS_COLORS[row.account_status] || "default";
    return <Chip label={row.account_status_label} color={color} size="small" />;
  }

  // For internal users or if no account_status, show regular status
  if (row.status === 0) {
    return <Chip label={intl.get("Inactive").d("Inactive")} color="default" size="small" />;
  }
  return <Chip label={intl.get("Active").d("Active")} color="success" size="small" />;
};

/**
 * Build account status options for the select filter
 * @returns {Array<{value: number, label: string}>}
 */
const getAccountStatusFilterOptions = () => {
  return ACCOUNT_STATUS_OPTIONS.map((option) => ({
    value: option.value,
    label: intl.get(option.label).d(option.label),
  }));
};

/**
 * User table component using DmsEntityTable
 */
const UserTable = forwardRef(({
  onViewDetails,
  onBlock,
  onUnblock,
  onCreate,
  onChangePassword = null,
  userType = undefined,
  showStatusFilter = false,
}, ref) => {

  // Column definitions
  const columns = useMemo(() => {
    const cols = [
      {
        label: intl.get("Name").d("Name"),
        column_name: "user_name",
        filter: true,
      },
      {
        label: intl.get("Email").d("Email"),
        column_name: "user_email",
        filter: true,
      },
    ];

    // Status column - with or without select filter
    if (showStatusFilter) {
      cols.push({
        label: intl.get("Status").d("Status"),
        column_name: "is_blocked",
        width: "180px",
        function: (_value, row) => getStatusChip(row),
        filter: true,
        control: "select",
        options: getAccountStatusFilterOptions(),
        properties: { filter: { field: "account_status" } },
      });
    } else {
      cols.push({
        label: intl.get("Status").d("Status"),
        column_name: "is_blocked",
        width: "100px",
        element: getStatusChip,
      });
    }

    cols.push(
      {
        label: intl.get("Roles").d("Roles"),
        column_name: "roles",
        width: "200px",
        sortable: false,
        element: (row) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {(row.roles || []).map((role, idx) => (
              <Chip key={idx} label={intl.get(`RoleName.${role}`).d(role)} size="small" variant="outlined" />
            ))}
          </Box>
        ),
      },
      {
        label: intl.get("Institutions").d("Institutions"),
        column_name: "institution_name",
        width: "200px",
        sortable: false,
        filter: true,
        function: (_value, row) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {(row.institutions || []).slice(0, 2).map((inst, idx) => (
              <Chip key={idx} label={inst} size="small" variant="outlined" />
            ))}
            {(row.institutions || []).length > 2 && (
              <Chip label={`+${row.institutions.length - 2}`} size="small" />
            )}
            {(row.institutions || []).length === 0 && "-"}
          </Box>
        ),
      },
    );

    return cols;
  }, [showStatusFilter]);

  // Create button label based on user type
  const createButtonLabel = useMemo(() => {
    const key = CREATE_BUTTON_LABELS[userType] || CREATE_BUTTON_LABELS.default;
    return intl.get(key).d(CREATE_BUTTON_DEFAULTS[key]);
  }, [userType]);

  // Pre-filter based on user type (map frontend type to backend filter value)
  const preFilter = useMemo(() => {
    if (!userType) return null;
    const backendType = USER_TYPE_TO_BACKEND[userType] || userType;
    return [{ name: "user_type", value: backendType }];
  }, [userType]);

  // Check if user is not blocked (active)
  const isUserActive = useCallback((row) => !row.is_blocked, []);

  // Custom actions renderer when password change is enabled
  const customActionsRenderer = useMemo(() => {
    if (!onChangePassword) return null;

    return ({ row, isActive, isMobile }) => (
      <DmsButtonGroup
        sx={{
          '& .MuiIconButton-root, & .MuiButton-root': {
            minHeight: isMobile ? '44px' : '36px',
            minWidth: isMobile ? '44px' : '36px',
          }
        }}
      >
        {onViewDetails && (
          <DmsButton
            icon={<VisibilityIcon />}
            title={intl.get("ViewDetails").d("View Details")}
            onClick={() => onViewDetails(row)}
          />
        )}
        {isActive ? (
          onBlock && (
            <DmsButton
              icon={<BlockIcon />}
              title={intl.get("BlockUser").d("Block User")}
              onClick={() => onBlock(row)}
              color="warning"
            />
          )
        ) : (
          onUnblock && (
            <DmsButton
              icon={<LockOpenIcon />}
              title={intl.get("UnblockUser").d("Unblock User")}
              onClick={() => onUnblock(row)}
              color="success"
            />
          )
        )}
        <DmsButton
          icon={<LockIcon />}
          title={intl.get("Changepassword").d("Change password")}
          onClick={() => onChangePassword(row)}
          color="warning"
        />
      </DmsButtonGroup>
    );
  }, [onViewDetails, onBlock, onUnblock, onChangePassword]);

  return (
    <DmsEntityTable
      ref={ref}
      url="/user_management/table"
      columns={columns}
      preFilter={preFilter}
      onView={onViewDetails}
      onBlock={onBlock}
      onUnblock={onUnblock}
      onCreate={onCreate}
      isActiveCheck={isUserActive}
      createButtonLabel={createButtonLabel}
      blockTitle={intl.get("BlockUser").d("Block User")}
      unblockTitle={intl.get("UnblockUser").d("Unblock User")}
      actionsRenderer={customActionsRenderer}
      actionsWidth={onChangePassword ? "200px" : "150px"}
      hidePdf={true}
      hideExport={true}
    />
  );
});

UserTable.displayName = "UserTable";

UserTable.propTypes = {
  onViewDetails: PropTypes.func.isRequired,
  onBlock: PropTypes.func.isRequired,
  onUnblock: PropTypes.func.isRequired,
  onCreate: PropTypes.func,
  onChangePassword: PropTypes.func,
  userType: PropTypes.oneOf(["sso", "doctor", "pharmacist", "person", "auditor", "distributor_responsible"]),
  showStatusFilter: PropTypes.bool,
};


export { UserTable };
